import { EventEmitter, Injectable, Injector } from "@angular/core";

import { Observable, Subscription } from "rxjs";
import { Configuration, FormType, Roles, WorkRequired } from "src/app/common/enumerations/enumerations";
import { ResourcesService } from "src/app/components/catalogs/beamline-catalog/resource/resources.service";
import { User } from "src/app/components/catalogs/user-catalog/services/user";
import { UserService } from "src/app/components/catalogs/user-catalog/services/user.service";
import { WFTaskSignatureResource, SaveTaskResource, WfSignature, WfSignatureResource } from '../workflow/workflow';
import { SCFMaster, SCFHoldPointResource, SaveFormViewStateResource } from "./scf";
import { SiteConfigurationService } from "src/app/components/site-configuration/site-configuration.service";
import { utils } from "src/app/modules/libs/utils";
import { BaseService } from "src/app/common/base/base.service";
import { IFormService } from "src/app/common/interfaces/i-form-service";
import { FormBaseV2 } from "src/app/common/models/model-base";
import { Form } from "src/app/services/forms/form";

@Injectable({
  providedIn: "root",
})
export class ScfV2Service extends BaseService<SCFMaster, number> implements IFormService {

  CheckSelectedSection1: EventEmitter<any> = new EventEmitter<any>();
  SCFediting?: number | null = null;
  AmendmentSwitch = false;
  amendmentsSet: Set<number> = new Set<number>();
  copyDoc: SCFMaster | null = null;
  SerialNumber = "/SerialNumbers/" + FormType.SCF;
  Locations = "/Locations";
  Api = "/SCFMasterV2";
  ApiRestrictions = "/SCFRestriction";
  user?: User;

  constructor(
    protected override injector: Injector,
    private siteConfiguration: SiteConfigurationService,
    private resourcesService: ResourcesService,
    private userService: UserService,
  ) {
    super(injector, '/SCFMasterV2');
  }

  confirmSign(form: Form | FormBaseV2 | undefined, task: WfSignature, action?: number, data?: any, options?: string): Promise<boolean> {
    throw new Error("Method not implemented.");
  }
  save(form: Form | FormBaseV2): Promise<FormBaseV2 | undefined> {
    throw new Error("Method not implemented.");
  }
  saveTask(s: SaveTaskResource): Observable<any> {
    throw new Error("Method not implemented.");
  }
  setAmendmentRequesters(wfSignatures: WfSignatureResource[]): WfSignatureResource[] {
    throw new Error("Method not implemented.");
  }

  // Gets the Serial Number of the Form
  getSerialNo(): Observable<SerialNumber> {
    const httpresult1 = this.http.get<SerialNumber>(
      this.BASE_URL + this.SerialNumber
    );
    return httpresult1;
  }

  post(scfMaster: SCFMaster) {
    return this.http.post<SCFMaster>(this.BASE_URL + this.Api, scfMaster);
  }

  put(scfMaster: SCFMaster, refresh: boolean) {
    return this.http.put<SCFMaster>(
      this.BASE_URL + this.Api + "/" + refresh,
      scfMaster
    );
  }

  cancel(scfMaster: SCFMaster, reason: string) {
    return this.http.patch(this.BASE_URL + this.Api + "/cancel", {
      id: scfMaster.id,
      reason,
    });
  }

  cancelAmendment(scfMaster: SCFMaster, reason: string) {
    return this.http.patch(this.BASE_URL + this.Api + "/cancelAmendment", {
      id: scfMaster.id,
      reason,
    });
  }

  sign(taskSignature: WFTaskSignatureResource): Observable<SCFMaster> {
    const url: string = this.BASE_URL + this.Api + "/Sign";
    const httpresult = this.http.put<any>(url, taskSignature);
    return httpresult;
  }

  /**
   * Tester tooling (non-production): signs a task on behalf of the first user holding one
   * of the task's roles, instead of the logged-in user. The endpoint is non-prod gated and
   * additionally requires a System Administrator caller. See SCFMasterV2Controller.TestSign.
   */
  testSign(taskSignature: WFTaskSignatureResource): Observable<SCFMaster> {
    const url: string = this.BASE_URL + this.Api + "/TestSign";
    return this.http.put<any>(url, taskSignature);
  }

  /**
   * Tester tooling (non-production): advances an SCF to a target status by applying every required
   * signature in workflow order, server-side, in a single request (one reload instead of N). The
   * endpoint is non-prod gated and additionally requires a System Administrator caller.
   * See SCFMasterV2Controller.TestAdvance. `targetStatus` is a FormStatusEnum value.
   */
  testAdvance(scfMasterId: number, targetStatus: number): Observable<SCFMaster> {
    const url: string = this.BASE_URL + this.Api + "/TestAdvance/" + scfMasterId + "/" + targetStatus;
    return this.http.put<any>(url, null);
  }

  // save(s: SaveTaskResource) {
  //   const httpresult = this.http.put<any>(
  //     this.BASE_URL + this.Api + "/Save/" + s.id,
  //     s
  //   );
  //   return httpresult;
  // }

  saveTasks(saveTasks: SaveTaskResource[]) {
    const httpresult = this.http.put<any>(
      this.BASE_URL + this.Api + "/SaveTasks",
      saveTasks
    );
    return httpresult;
  }

  copySCF(id: number, isAmendment: boolean): Observable<SCFMaster> {
    return this.http.post<SCFMaster>(
      this.BASE_URL + this.Api + "/copy/" + id + "/" + isAmendment,
      null
    );
  }

  putStatus(saveFormViewState: SaveFormViewStateResource) {
    return this.http.put<SCFMaster>(
      this.BASE_URL + this.Api + "/SetStatus/" + saveFormViewState.id,
      saveFormViewState
    );
  }

  setEditing(id: number, value?: boolean): Observable<number> {
    const URL = this.BASE_URL + this.Api + "/SetEditing/" + id;
    const httpresult2 = this.http.put<number>(URL, { Value: value });
    return httpresult2;
  }

  // Amendments
  requestAmendment(id: number): Observable<any> {
    return this.http.put<any>(
      this.BASE_URL + this.Api + "/RequestAmendment/" + id.toString(),
      null
    );
  }

  finishAmendment(id: number): Observable<any> {
    return this.http.put<any>(
      this.BASE_URL + this.Api + "/FinishAmendment/" + id.toString(),
      null
    );
  }

  async getEmails(scf: SCFMaster) {
    const notifiedRoleIDs: number[] = [];
    const emails: string[] = [];
    const configs = await this.siteConfiguration.getAll().toPromise();
    // Default
    const scfNotifications = configs?.find(x => x.id == Configuration.SCFNotifications)?.value as string;
    const scfEmails = configs?.find(x => x.id == Configuration.SCFEmails)?.value as string;
    const scfHPEmails = configs?.find(x => x.id == Configuration.SCFHPEmails)?.value as string;

    if (['True', 'true'].includes(scfNotifications)) {
      scfEmails.split(",")?.forEach((e) => utils.pushNoRepeat(emails, e.trim()));
    }
    // HP
    if (scf.notifyHP) {
      scfEmails?.split(",")?.forEach((e) => utils.pushNoRepeat(emails, e.trim()));
    }
    // BLSCI
    if (scf.notifyBLSci) {
      const notifiedUsers: number[] = [];
      scf.locations?.map((loc) => notifiedUsers.push(loc.id ?? 0));
      const relUsers = await this.resourcesService
        .getResourceUserRelationsById(notifiedUsers)
        .toPromise();
      relUsers?.map((related) => {
        if (
          related.roleId == Roles.BLSCI ||
          related.roleId === Roles.ALTBLSCI
        ) {
          if (
            related?.user?.emailAddress &&
            related?.user.emailAddress.trim() !== ""
          ) {
            utils.pushNoRepeat(emails, related.user.emailAddress);
          }
        }
      });
    }
    if (scf.notifyBRC) {
      notifiedRoleIDs.push(Roles.BRC);
    }
    if (scf.notifyRF) {
      notifiedRoleIDs.push(Roles.RFEng);
    }
    if (scf.notifySnA) {
      notifiedRoleIDs.push(Roles.SnA);
    }
    if (scf.notifyARC) {
      notifiedRoleIDs.push(Roles.ARC);
    }
    // RP
    if (scf.notifyRP) {
      (configs?.find(x => x.id == Configuration.SCFRPEmails)?.value as string)?.split(",")?.forEach((e) => utils.pushNoRepeat(emails, e.trim()));
    }

    // By Role
    const users = await this.userService
      .ReadByRole(notifiedRoleIDs)
      .toPromise();
    users?.map((e) => {
      if (e.emailAddress && e.emailAddress.trim() !== "") {
        utils.pushNoRepeat(emails, e.emailAddress.trim());
      }
    });

    // Requester
    this.user = this.users.find((x) => x.id == scf.requesterId);
    utils.pushNoRepeat(emails, this.user?.emailAddress?.trim());

    return emails;
  }

  // Hold Points
  createHoldPoint(
    holdPoint: SCFHoldPointResource
  ): Observable<SCFHoldPointResource> {
    return this.http.post<SCFHoldPointResource>(
      this.BASE_URL + this.Api + "/CreateHoldPoint",
      holdPoint
    );
  }

  updateHoldPoint(
    holdPoint: SCFHoldPointResource
  ): Observable<SCFHoldPointResource> {
    return this.http.put<SCFHoldPointResource>(
      this.BASE_URL + this.Api + "/UpdateHoldPoint",
      holdPoint
    );
  }

  updateHoldPointNumbers(holdPoints: SCFHoldPointResource[]): Observable<SCFHoldPointResource> {
    return this.http.put<SCFHoldPointResource>(this.BASE_URL + this.Api + "/UpdateHoldPointNumbers", holdPoints);
  }

  deleteHoldPoint(holdPoint: SCFHoldPointResource) {
    return this.http.delete(
      this.BASE_URL + this.Api + "/DeleteHoldPoint/" + holdPoint.id
    );
  }


}

class SerialNumber {
  serialNo!: string;
}

export class SCFPrivileges {
  canCreate!: boolean | null;
  canEdit!: boolean | null;
  canSubmit!: boolean | null;
  canUnsubmit!: boolean | null;
  canCancel!: boolean | null;
  canViewAmendment!: boolean | null;
  canCreateAmendment!: boolean | null;
  canEditAmendment!: boolean | null;
  canSubmitAmendment!: boolean | null;
  canUnsubmitAmendment!: boolean | null;
  canCancelAmendment!: boolean | null;
  canDuplicate!: boolean | null;
  canRelease!: boolean | null;
  canAddHoldPoints!: boolean | null;
  canAddHoldPointsDraft!: boolean | null;
  canDisapprove!: boolean | null;
}


export class SCFWorkRequired {
  workRequired?: WorkRequired;
  checked!: boolean | null;
}
